import { useMutation } from '@tanstack/react-query';
import { Info, Sparkles, UserCheck } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type AutoAssignResult,
  type Estimate,
  type TaskPriority,
  isDetailedEnough,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { errorMessage } from '../../../lib/error-message';
import { EstimateCard } from '../../ai/components/estimate-card';
import { useAiRunConfirm } from '../../ai/components/ai-run-confirm';
import { useAiCapabilities } from '../../ai/use-ai-capabilities';
import { useTrackedAiRun } from '../../ai/use-ai-run';
import { taskFormApi } from '../api';

/**
 * Dòng "Dùng AI giao việc" của form việc — APM-AIA-d.
 *
 * Ô **Story point** + nút **Estimate Storypoint** + nút **Auto Assign**. Hiện khi
 * PM tích ô "Dùng AI giao việc" (người dùng chốt 2026-09-25: chỉ PM, vì Auto
 * Assign đặt người **và** ngày).
 *
 * ## Chỉ gợi ý (luật #6)
 *
 * Hai nút chỉ **điền ô** của form. Không gì được ghi cho tới khi người bấm Lưu
 * — và mọi ô vẫn sửa tay được sau khi AI điền, kể cả số điểm.
 *
 * ## Cửa "đủ chi tiết" kiểm ở đây trước
 *
 * `isDetailedEnough` — đúng hàm server gọi. Chưa đủ thì nói ngay, không tốn một
 * lượt mạng và không mở hộp xác nhận chi phí cho một lượt chạy chắc chắn bị từ
 * chối.
 *
 * ## Không có key AI vẫn chạy
 *
 * Server trả bản tất định (trung vị việc tương tự; người xong sớm nhất). Nên khi
 * chưa có key thì gọi thẳng API — **không** qua `useAiRunConfirm`, vì hộp ấy khi
 * thiếu key sẽ hỏi key thay vì chạy, và người dùng mất đúng thứ vẫn làm được.
 */
export function TaskFormAiAssist({
  projectId,
  title,
  description,
  checklist,
  taskTypeId,
  priority,
  storyPoints,
  onStoryPoints,
  hours,
  onAssigned,
}: {
  readonly projectId: string;
  readonly title: string;
  /** HTML của trình soạn thảo — server tự bỏ thẻ. */
  readonly description: string;
  /** Chữ của từng mục checklist đang soạn. */
  readonly checklist: readonly string[];
  readonly taskTypeId: string;
  readonly priority: TaskPriority;
  /** Ô story point là của form (nó được lưu cùng việc) — ở đây chỉ đọc và điền. */
  readonly storyPoints: string;
  readonly onStoryPoints: (value: string) => void;
  /**
   * Ô "Thời gian làm (giờ)" của form. Gửi kèm Auto Assign: dự án chưa có tốc độ
   * theo story point thì server xếp đúng số giờ này ra hạn chót (APM-AIA-d).
   */
  readonly hours: string;
  /** Điền Người làm, Thời gian làm, Bắt đầu, Hạn chót — form quyết cách áp. */
  readonly onAssigned: (result: AutoAssignResult) => void;
}): ReactNode {
  const { t } = useTranslation();
  const track = useTrackedAiRun();
  const aiConfirm = useAiRunConfirm(projectId);
  const capabilities = useAiCapabilities(projectId);
  const aiEnabled = capabilities.data?.enabled === true;

  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [assigned, setAssigned] = useState<AutoAssignResult | null>(null);
  /** Nút nào vừa bị chặn vì thiếu gì — lời nhắc tự tắt khi điều kiện đã đủ. */
  const [hint, setHint] = useState<'detail' | 'points' | null>(null);

  const detailed = isDetailedEnough({ description, checklist });
  const points = Number(storyPoints);
  const hasPoints = storyPoints.trim() !== '' && Number.isInteger(points) && points > 0;
  const typedHours = hours.trim() !== '' && Number(hours) > 0 ? Number(hours) : null;
  const base = {
    title: title.trim(),
    ...(description.trim() === '' ? {} : { description }),
    ...(checklist.length === 0 ? {} : { checklist: [...checklist] }),
  };

  const estimatePoints = useMutation({
    mutationFn: () => {
      const call = (runId?: string) =>
        taskFormApi.estimatePoints(projectId, { ...base, ...(runId ? { runId } : {}) });
      return aiEnabled
        ? track({ skill: 'estimate_story_points', label: t('taskForm.ai.estimate') }, call)
        : call();
    },
    onSuccess: (result) => {
      if (result.estimate === null) {
        toast.info(t('taskForm.ai.noEstimate'));
        return;
      }
      setEstimate(result.estimate);
      onStoryPoints(String(result.estimate.value));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const autoAssign = useMutation({
    mutationFn: () => {
      const call = (runId?: string) =>
        taskFormApi.autoAssign(projectId, {
          ...base,
          storyPoints: points,
          ...(typedHours === null ? {} : { estimateHours: typedHours }),
          ...(taskTypeId === '' ? {} : { taskTypeId }),
          priority,
          ...(runId ? { runId } : {}),
        });
      return aiEnabled
        ? track({ skill: 'auto_assign_task', label: t('taskForm.ai.autoAssign') }, call)
        : call();
    },
    onSuccess: (result) => {
      setAssigned(result);
      if (result.assigneeId === null) {
        toast.info(result.reason);
        return;
      }
      onAssigned(result);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const run = (skill: string, label: string, go: () => void): void => {
    if (!detailed) {
      setHint('detail');
      return;
    }
    if (aiEnabled) aiConfirm.confirm(skill, label, go);
    else go();
  };

  return (
    <div className="flex flex-col gap-2 rounded-DEFAULT border border-border/70 bg-muted/20 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          {t('taskForm.ai.storyPoints')}
          <span className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              step={1}
              value={storyPoints}
              onChange={(event) => onStoryPoints(event.target.value)}
              className="h-9 w-20 rounded-DEFAULT border border-input bg-background px-2 text-right text-sm tabular-nums"
            />
            {/* ⓘ chỉ có khi đã có lý do để xem — một nút mở ra khoảng trống là một nút hỏng. */}
            {estimate !== null ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('taskForm.ai.why')}
                    title={t('taskForm.ai.why')}
                    className="inline-flex size-7 items-center justify-center text-muted-foreground hover:text-foreground rounded-full glass-hover"
                  >
                    <Info aria-hidden className="size-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <EstimateCard estimate={estimate} />
                </PopoverContent>
              </Popover>
            ) : null}
          </span>
        </label>

        <Button
          type="button"
          variant="outline"
          disabled={estimatePoints.isPending}
          onClick={() =>
            run('estimate_story_points', t('taskForm.ai.estimate'), () => estimatePoints.mutate())
          }
        >
          <Sparkles aria-hidden className="mr-1 size-4" />
          {estimatePoints.isPending ? t('taskForm.ai.working') : t('taskForm.ai.estimate')}
        </Button>

        <Button
          type="button"
          variant="outline"
          disabled={autoAssign.isPending}
          onClick={() => {
            if (detailed && !hasPoints) {
              setHint('points');
              return;
            }
            run('auto_assign_task', t('taskForm.ai.autoAssign'), () => autoAssign.mutate());
          }}
        >
          <UserCheck aria-hidden className="mr-1 size-4" />
          {autoAssign.isPending ? t('taskForm.ai.working') : t('taskForm.ai.autoAssign')}
        </Button>
        {aiConfirm.dialog}
      </div>

      {hint === 'detail' && !detailed ? (
        <p role="alert" className="text-xs text-rag-amber">
          {t('errors.TASK_NOT_DETAILED_ENOUGH')}
        </p>
      ) : null}
      {hint === 'points' && !hasPoints ? (
        <p role="alert" className="text-xs text-rag-amber">
          {t('taskForm.ai.needPoints')}
        </p>
      ) : null}

      {assigned !== null && assigned.assigneeId !== null ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {t('taskForm.ai.assignedTo', { name: assigned.assigneeName })}
          </span>{' '}
          {assigned.reason}{' '}
          {assigned.pace === null
            ? assigned.estimateHours !== null
              ? t('taskForm.ai.typedHours', { hours: assigned.estimateHours })
              : t('taskForm.ai.noPace')
            : t(`taskForm.ai.pace.${assigned.pace.basis}`, {
                hours: assigned.pace.hoursPerPoint,
                count: assigned.pace.sampleSize,
              })}
          {/* Có giờ mà không có hạn chót: 90 ngày tới không đủ chỗ trống cho chừng ấy giờ. */}
          {assigned.estimateHours !== null && assigned.dueDate === null
            ? ` ${t('taskForm.ai.noRoom')}`
            : ''}
        </p>
      ) : null}
    </div>
  );
}
